import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CreateStepperLayout, { StepperLayoutContent } from 'layouts/CreateStepperLayout';

import Button from 'primitives/Button';

import StepReview from './StepReview';
import StepScope, { ScopeStepData } from './StepScope';
import StepTransferee, { TransfereeStepData } from './StepTransferee';
import StepUnitsLocation, { UnitsLocationStepData } from './StepUnitsLocation';

type WizardData = ScopeStepData & TransfereeStepData & UnitsLocationStepData;

const INITIAL_DATA: WizardData = {
  transferorInvestmentEntityId: '',
  transferorInvestmentEntityName: '',
  reason: '',
  reasonDescription: '',
  relationship: '',
  scope: '',
  closingIds: [],
  transfereeUserId: '',
  transfereeName: '',
  transfereeMaskedEmail: '',
  unitsLocation: '',
};

const STEPS = [
  { key: 'scope', title: 'Reason & scope' },
  { key: 'transferee', title: 'Transferee' },
  { key: 'unitsLocation', title: 'Units location' },
  { key: 'review', title: 'Review & submit' },
];

const NewUnitTransferRequest: FC = () => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [data, setData] = useState<WizardData>(INITIAL_DATA);
  const [draftRequestId, setDraftRequestId] = useState<string | null>(null);
  // Steps that gate on something other than FormPanel validation (a confirmed transferee lookup,
  // a successfully created draft) report readiness here. Without it their buttonRef.submit()
  // silently no-ops and the button looks broken.
  const [stepReady, setStepReady] = useState(true);
  const buttonRef = useRef<any>(null);

  const isReviewStep = stepIndex === STEPS.length - 1;
  const isLastEditableStep = stepIndex === STEPS.length - 2;

  function goNext(stepData: Partial<WizardData>) {
    setData(previous => ({ ...previous, ...stepData }));
    setStepReady(true);
    setStepIndex(previous => Math.min(previous + 1, STEPS.length - 1));
  }

  function goBack() {
    setStepReady(true);
    setStepIndex(previous => Math.max(previous - 1, 0));
  }

  function renderStep() {
    switch (STEPS[stepIndex].key) {
      case 'scope':
        return <StepScope data={data} onSubmit={goNext} buttonRef={buttonRef} />;
      case 'transferee':
        return (
          <StepTransferee
            data={data}
            onSubmit={goNext}
            onReadyChange={setStepReady}
            buttonRef={buttonRef}
          />
        );
      case 'unitsLocation':
        return <StepUnitsLocation data={data} onSubmit={goNext} buttonRef={buttonRef} />;
      case 'review':
        return (
          <StepReview
            data={data}
            draftRequestId={draftRequestId}
            onDraftCreated={setDraftRequestId}
            onReadyChange={setStepReady}
            onSubmitted={id => navigate(`/invest/unit-transfers/${id}`)}
            buttonRef={buttonRef}
          />
        );
      default:
        return null;
    }
  }

  return (
    <CreateStepperLayout onClose={() => navigate(-1)}>
      <h2 className="mt-6 text-2xl font-bold leading-9 tracking-tight text-gray-700">
        New unit transfer request
      </h2>
      <p className="text-md mt-1 text-gray-500">
        Step {stepIndex + 1} of {STEPS.length}: {STEPS[stepIndex].title}
      </p>
      <StepperLayoutContent>
        {renderStep()}
        <div className="col-span-full flex items-center justify-between gap-3 mt-6">
          {stepIndex > 0 && !draftRequestId ? (
            <Button variant="secondary" onClick={goBack}>
              Back
            </Button>
          ) : (
            <div />
          )}
          <Button disabled={!stepReady} onClick={() => buttonRef.current?.submit()}>
            {isReviewStep ? 'Submit request' : isLastEditableStep ? 'Review' : 'Next'}
          </Button>
        </div>
      </StepperLayoutContent>
    </CreateStepperLayout>
  );
};

export default NewUnitTransferRequest;
